import { Link } from "react-router-dom";
import { ArrowRight, Ambulance, HeartPulse, Star, ShieldCheck, MessageCircle } from "lucide-react";
import { motion, HeroFade } from "@/lib/motion";
import { SliderHero } from "./SliderHero";

/** Style 1 — "layered": asymmetric image with floating trust badges (current default) */
function LayeredHero({ site, doctors, phone, whatsapp }) {
  const heroTitle = site?.hero_title || "Compassionate Care, Trusted Expertise";
  const parts = heroTitle.split(/,\s*/);
  const first = parts[0];
  const rest = parts.slice(1).join(", ");

  return (
    <section className="relative overflow-hidden" data-testid="hero-layered">
      <div className="absolute inset-0 bg-gradient-to-br from-red-50/70 via-white to-blue-50/40" aria-hidden />
      <div className="absolute inset-0 opacity-[0.15]" aria-hidden style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, rgba(185,28,28,0.4) 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }} />
      <motion.div className="pointer-events-none absolute -top-24 -right-20 w-[500px] h-[500px] rounded-full bg-red-200/30 blur-3xl"
        animate={{ y: [0, -20, 0] }} transition={{ repeat: Infinity, duration: 9, ease: "easeInOut" }} aria-hidden />
      <motion.div className="pointer-events-none absolute -bottom-40 -left-24 w-[520px] h-[520px] rounded-full bg-blue-200/30 blur-3xl"
        animate={{ y: [0, 24, 0] }} transition={{ repeat: Infinity, duration: 11, ease: "easeInOut" }} aria-hidden />

      <div className="container-medi relative pt-14 pb-20 lg:pt-20 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <HeroFade>
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur ring-1 ring-red-100 rounded-full px-4 py-1.5 mb-6 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-semibold tracking-wide text-slate-700 uppercase">{site?.hero_badge || "Trusted Healthcare Since 2001"}</span>
            </div>
          </HeroFade>
          <HeroFade delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-heading font-semibold tracking-tight text-slate-900 leading-[1.05]">
              {first}{rest ? "," : ""}<br />
              {rest && <span className="bg-gradient-to-r from-red-700 via-red-600 to-red-800 bg-clip-text text-transparent">{rest}.</span>}
            </h1>
          </HeroFade>
          <HeroFade delay={0.2}>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              {site?.hero_subtitle || "24/7 emergency care, modern diagnostics and a team of specialists dedicated to putting patients first."}
            </p>
          </HeroFade>
          <HeroFade delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/doctors" className="btn-primary group" data-testid="hero-find-doctor">
                Find a Doctor <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-full px-6 py-3 shadow-medi-md transition" data-testid="hero-whatsapp">
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </HeroFade>
          <HeroFade delay={0.4}>
            <div className="mt-8 flex flex-wrap gap-6 items-center">
              <div className="flex -space-x-3">
                {doctors.slice(0, 4).map((d) => d.photo_url ? <img key={d.id} src={d.photo_url} alt={d.name} className="w-10 h-10 rounded-full ring-2 ring-white object-cover" /> : null)}
              </div>
              <div>
                <div className="flex items-center gap-1 text-amber-500">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" strokeWidth={0} />)}
                  <span className="ml-1.5 text-sm font-semibold text-slate-900">4.9</span>
                </div>
                <div className="text-xs text-slate-500">Rated by 2,300+ patients</div>
              </div>
            </div>
          </HeroFade>
          <HeroFade delay={0.5}>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md border-t border-slate-200/80 pt-6">
              {[{ n: "25+", l: "Years of Care" }, { n: "50+", l: "Specialists" }, { n: "24/7", l: "Emergency" }].map((s) => (
                <div key={s.l}>
                  <div className="text-2xl lg:text-3xl font-heading font-semibold text-red-700">{s.n}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </HeroFade>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.94, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} className="lg:col-span-6 relative">
          <div className="relative aspect-[5/6] max-h-[620px]">
            <div className="absolute inset-4 rounded-[2.5rem] border-2 border-red-200/60" aria-hidden />
            <img src={site?.hero_image || "https://images.unsplash.com/photo-1587351021821-f871837248c6?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"}
              alt="Modern hospital building" className="absolute inset-0 w-full h-full object-cover rounded-[2.5rem] shadow-medi-xl" data-testid="hero-image" />
            <motion.div initial={{ opacity: 0, x: -30, y: 20 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ duration: 0.7, delay: 1 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-medi-lg p-4 flex items-center gap-3 max-w-[260px]">
              <motion.div className="w-11 h-11 rounded-full bg-red-100 grid place-items-center flex-shrink-0" animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}>
                <HeartPulse className="w-5 h-5 text-red-700" />
              </motion.div>
              <div>
                <div className="text-sm font-semibold text-slate-900">100,000+ Patients</div>
                <div className="text-xs text-slate-500">Cared for since 2001</div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30, y: -20 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ duration: 0.7, delay: 1.15 }}
              className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-medi-lg px-4 py-3 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <div><div className="text-xs text-slate-500">Certified</div><div className="text-sm font-semibold text-slate-900">NABL Lab</div></div>
            </motion.div>
            <motion.a href={`tel:${phone}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 1.3 }}
              className="absolute right-[-24px] top-1/2 -translate-y-1/2 bg-red-700 hover:bg-red-800 text-white rounded-2xl shadow-medi-xl px-4 py-3 flex items-center gap-2.5 transition" data-testid="hero-emergency">
              <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Ambulance className="w-4 h-4" /></div>
              <div><div className="text-[10px] uppercase tracking-widest text-red-100">Emergency</div><div className="text-sm font-semibold">{phone}</div></div>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/** Style 2 — "split": bold dark side + full-bleed image, editorial magazine feel */
function SplitHero({ site, doctors, phone, whatsapp }) {
  return (
    <section className="relative bg-slate-950 text-white overflow-hidden" data-testid="hero-split">
      <div className="grid lg:grid-cols-2">
        {/* LEFT DARK PANEL */}
        <div className="relative py-16 lg:py-24 px-6 sm:px-10 lg:px-16 order-2 lg:order-1">
          <motion.div className="pointer-events-none absolute -top-24 -left-16 w-[400px] h-[400px] rounded-full bg-red-700/25 blur-3xl"
            animate={{ y: [0, -20, 0] }} transition={{ repeat: Infinity, duration: 9 }} aria-hidden />
          <div className="relative max-w-xl">
            <HeroFade>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6 backdrop-blur">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-red-100">{site?.hero_badge || "Trusted Healthcare Since 2001"}</span>
              </div>
            </HeroFade>
            <HeroFade delay={0.1}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold leading-[1.05]">
                {(site?.hero_title || "Compassionate Care, Trusted Expertise.").split(/,\s*/).map((chunk, i, arr) => (
                  <span key={i} className={i === arr.length - 1 && arr.length > 1 ? "text-red-400" : ""}>
                    {chunk}{i < arr.length - 1 ? "," : "."}<br />
                  </span>
                ))}
              </h1>
            </HeroFade>
            <HeroFade delay={0.2}>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-lg">
                {site?.hero_subtitle || "24/7 emergency care, modern diagnostics and a team of specialists dedicated to putting patients first."}
              </p>
            </HeroFade>
            <HeroFade delay={0.3}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/doctors" className="inline-flex items-center gap-2 bg-white text-slate-950 hover:bg-red-50 font-semibold rounded-full px-6 py-3 transition group" data-testid="hero-find-doctor">
                  Find a Doctor <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-full px-6 py-3 transition" data-testid="hero-whatsapp">
                  <MessageCircle className="w-4 h-4" /> WhatsApp Us
                </a>
              </div>
            </HeroFade>
            <HeroFade delay={0.4}>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md border-t border-white/10 pt-6">
                {[{ n: "25+", l: "Years" }, { n: "50+", l: "Doctors" }, { n: "24/7", l: "Emergency" }].map((s) => (
                  <div key={s.l}>
                    <div className="text-2xl lg:text-3xl font-heading font-semibold text-white">{s.n}</div>
                    <div className="text-xs text-slate-400 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </HeroFade>
          </div>
        </div>

        {/* RIGHT IMAGE — full bleed */}
        <div className="relative min-h-[420px] lg:min-h-[720px] order-1 lg:order-2 overflow-hidden">
          <motion.img initial={{ scale: 1.05 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            src={site?.hero_image || "https://images.unsplash.com/photo-1587351021821-f871837248c6?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"}
            alt="Hospital" className="absolute inset-0 w-full h-full object-cover" data-testid="hero-image" />
          <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/70 via-slate-950/20 to-transparent lg:from-slate-950 lg:via-slate-950/40 lg:to-transparent" />

          {/* Floating emergency card */}
          <motion.a href={`tel:${phone}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8 }}
            className="absolute bottom-6 right-6 lg:bottom-10 lg:right-10 bg-red-700 hover:bg-red-800 text-white rounded-2xl shadow-medi-xl px-5 py-4 flex items-center gap-3 transition" data-testid="hero-emergency">
            <motion.div className="w-11 h-11 rounded-full bg-white/20 grid place-items-center" animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 1.6 }}>
              <Ambulance className="w-5 h-5" />
            </motion.div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-red-100">24/7 Emergency</div>
              <div className="text-lg font-semibold">{phone}</div>
            </div>
          </motion.a>

          {/* Trust badge */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 1 }}
            className="absolute top-6 left-6 lg:top-10 lg:left-10 bg-white/95 backdrop-blur text-slate-900 rounded-2xl shadow-medi-lg p-4 flex items-center gap-3 max-w-[260px]">
            <div className="flex -space-x-2">
              {doctors.slice(0, 3).map((d) => d.photo_url ? <img key={d.id} src={d.photo_url} alt={d.name} className="w-9 h-9 rounded-full ring-2 ring-white object-cover" /> : null)}
            </div>
            <div>
              <div className="flex items-center gap-1 text-amber-500">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" strokeWidth={0} />)}
                <span className="ml-1 text-xs font-semibold text-slate-900">4.9</span>
              </div>
              <div className="text-[11px] text-slate-500">2,300+ patients</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function Hero({ site, doctors }) {
  const phone = site?.phone || "0341-9300560";
  const whatsapp = site?.whatsapp || "923419300560";
  const style = site?.hero_style || "layered";
  if (style === "slider") return <SliderHero site={site} doctors={doctors} />;
  if (style === "split") return <SplitHero site={site} doctors={doctors} phone={phone} whatsapp={whatsapp} />;
  return <LayeredHero site={site} doctors={doctors} phone={phone} whatsapp={whatsapp} />;
}
